import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const links = [
  { label: 'About', href: '#about' },
  { label: 'Download', href: '#download' },
  { label: 'Company', href: '#company' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'py-3 glass border-b border-black/5'
          : 'py-5 bg-transparent border-b border-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_premium-coinsflu/artifacts/31ksmxsk_coinsflu.jpg"
            alt="CoinsFlu logo"
            className="w-9 h-9 rounded-xl shadow-sm group-hover:shadow-brand transition-shadow"
          />
          <span className="font-pixel text-lg tracking-[0.04em] text-ink">
            CoinsFlu
          </span>
        </Link>

        <ul className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-[15px] text-ink/80 hover:text-brand transition-colors relative after:absolute after:inset-x-0 after:-bottom-1.5 after:h-[2px] after:bg-brand after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <a
          href="#download"
          data-testid="nav-cta-download"
          className="hidden md:inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2.5 rounded-full font-medium btn-primary transition-all active:scale-95"
        >
          Download App
        </a>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          className="md:hidden w-10 h-10 rounded-full bg-white/80 border border-black/10 flex items-center justify-center"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden mx-5 mt-3 glass rounded-3xl p-5"
            data-testid="nav-mobile-menu"
          >
            <ul className="flex flex-col gap-1">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="block py-3 px-2 text-ink hover:bg-surface-muted rounded-xl"
                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href="#download"
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-cta-download"
                  className="mt-2 block text-center bg-brand text-white py-3 rounded-full font-medium"
                >
                  Download App
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
